import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from '../api.service';
import { map, Observable, of } from 'rxjs';
import {
  CreateOrgResponse,
  OrgQuotaData,
  OrgQuotaResponse,
  OrgsListData,
  OrgsListResponse,
} from '../../interfaces/org-management';

type CreateOrgData = {
  orgName: string;
  orgAddress: string;
  orgContact: string;
  adminName: string;
  adminEmail: string;
  region: string;
};

type EditOrgData = {
  orgId: string;
} & Partial<Omit<CreateOrgData, 'region'>>;

@Injectable({
  providedIn: 'root',
})
export class OrgService {
  constructor(private apiService: ApiService) {}

  createOrg({
    orgName,
    orgAddress,
    orgContact,
    adminName,
    adminEmail,
    region,
  }: CreateOrgData): Observable<ApiResponse<CreateOrgResponse>> {
    const createOrgData = {
      Name: orgName,
      Address: orgAddress,
      PrimaryContactPhone: orgContact,
      Admin: {
        Name: adminName,
        Email: adminEmail,
      },
      Region: region,
    };
    return this.apiService.post<ApiResponse<CreateOrgResponse>>(`/v1/organizations`, createOrgData);
  }

  editOrg({
    orgId,
    orgName,
    orgAddress,
    orgContact,
    adminName,
    adminEmail,
  }: EditOrgData): Observable<ApiResponse<Partial<CreateOrgResponse>>> {
    const editOrgData = {
      Name: orgName,
      Address: orgAddress,
      PrimaryContactPhone: orgContact,
      PrimaryContactName: adminName,
      PrimaryContactEmail: adminEmail,
    };

    return this.apiService.patch<ApiResponse<Partial<CreateOrgResponse>>>(`/v1/organizations/${orgId}`, editOrgData);
  }

  deleteOrg(orgId: string): Observable<any> {
    return this.apiService.delete(`/v1/organizations/${orgId}`);
  }

  getOrg(): Observable<any> {
    return this.apiService.get(`/v1/organizations`);
  }

  listOrgs(): Observable<OrgsListData[]> {
    return this.apiService.get<ApiResponse<OrgsListResponse[]>>(`/v1/organizations`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.map((org) => {
            return {
              orgId: String(org.OrgID),
              name: org.Name,
              address: org.Address,
              primaryContactPhone: org.PrimaryContactPhone,
              primaryContactName: org.PrimaryContactName,
              primaryContactEmail: org.PrimaryContactEmail,
              region: org.Region,
              bucketName: org.BucketName,
              domainName: org.DomainName,
              isArchived: org.IsArchived,
              deletedByUser: org.DeletedByUser,
              deletedOn: org.DeletedOn,
              quota: org.Quota,
              createdOn: org.CreatedOn,
              applications: org.Applications.map((app) => {
                return {
                  appId: app.AppID,
                  appName: app.AppName,
                  versionId: app.VersionID,
                  version: app.Version,
                };
              }),
            };
          });
        } else {
          throw new Error(response.message);
        }
      })
    );
  }

  /**
   * Get valid GCP regions for org creation. Static data for now.
   * Current default is us-central1. When more are available, they should be fetched from backend,
   * and the default updated. Or send 'null' on org creation, backend will use default.
   */
  getRegionsForOrg(): Observable<{ regions: string[] }> {
    // more dummy data for testing UI styles
    // const regions = ['another-one1', 'another-two1', 'us-central1', 'another-three1', 'another-four1'];
    const regions = ['us-central1', 'europe-west3'];
    return of({ regions });
  }

  getQuotas(): Observable<OrgQuotaData[]> {
    return this.apiService.get<ApiResponse<OrgQuotaResponse[]>>(`/v1/organizations/quotas`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.map((org) => ({
            orgId: String(org.OrgID),
            orgName: org.Name,
            quota: org.Quota,
          }));
        }
        throw new Error(response.message);
      })
    );
  }

  updateQuota(orgId: string, quota: number): Observable<ApiResponse<{ OrgID: number; Quota: number }>> {
    return this.apiService.patch(`/v1/organizations/${orgId}/quota`, { Quota: quota });
  }

  assignOrgToVersion(orgId: number, versionId: number): Observable<any> {
    return this.apiService.put(`/v1/organizations/${orgId}/app-versions/${versionId}`, {});
  }

  unassignOrgToVersion(orgId: number, versionId: number): Observable<any> {
    return this.apiService.delete(`/v1/organizations/${orgId}/app-versions/${versionId}`);
  }
}
